import React from 'react';
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { X, Clock, Code, MessageSquare, Brain, Award, BarChart3, ArrowRight, CheckCircle2 } from 'lucide-react';
import { formatInterviewDuration } from '../../utils/candidateUtils';

const InterviewDetailModal = ({ interview, isOpen, onClose }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  if (!isOpen || !interview) return null;

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-50 border-green-100';
    if (score >= 60) return 'text-orange-600 bg-orange-50 border-orange-100';
    return 'text-red-600 bg-red-50 border-red-100';
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return 'N/A';
    const date = new Date(dateTime);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString(i18n.language === 'en' ? 'en-US' : (i18n.language === 'hi' ? 'hi-IN' : i18n.language), {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateTime) => {
    if (!dateTime) return 'N/A';
    const date = new Date(dateTime);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleTimeString(i18n.language === 'en' ? 'en-US' : (i18n.language === 'hi' ? 'hi-IN' : i18n.language), {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Safely extract breakdown
  const breakdown = (() => {
    const raw = interview?.feedback?.detailed_breakdown;
    if (!raw) return {};
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch {
        return {};
      }
    }
    return typeof raw === 'object' ? raw : {};
  })();

  // Extract score
  const rawScore = breakdown?.effective_score ?? breakdown?.total_score ?? interview?.feedback?.total_score ?? interview?.total_score;
  const scoreNum = Number.isFinite(Number(rawScore)) ? Number(rawScore) : null;

  // Extract categories safely (can be in breakdown.categories or interview.feedback.categories)
  const rawCategories = breakdown?.categories || interview?.feedback?.categories || {};
  const categoriesObj = typeof rawCategories === 'object' && rawCategories !== null ? rawCategories : {};
  const categoryEntries = Object.entries(categoriesObj);

  // Extract summary safely
  const summary = interview?.feedback?.summary || breakdown?.summary || '';

  // Job title
  const jobTitle = interview?.job_description?.title || interview?.job_role || t('b2cDashboard.practiceInterview', 'AI Mock Interview');

  const durationStr = formatInterviewDuration(interview);
  const questionsCount = interview?.questions_count ?? breakdown?.qa_pairs?.length ?? 0;

  const getCategoryIcon = (category) => {
    const cat = category.toLowerCase();
    if (cat.includes('tech') || cat.includes('code')) return <Code className="w-5 h-5" />;
    if (cat.includes('comm') || cat.includes('verbal')) return <MessageSquare className="w-5 h-5" />;
    if (cat.includes('prob') || cat.includes('logic') || cat.includes('brain')) return <Brain className="w-5 h-5" />;
    if (cat.includes('lead') || cat.includes('score')) return <Award className="w-5 h-5" />;
    return <BarChart3 className="w-5 h-5" />;
  };

  return (
    <div 
      className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 overflow-y-auto transition-opacity"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] my-auto overflow-hidden shadow-2xl flex flex-col animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 sm:p-6 border-b border-gray-100 flex items-center justify-between bg-white shrink-0">
          <div className="min-w-0 pr-4">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate">
                {jobTitle}
              </h2>
              {interview?.status && (
                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                  interview.status === 'completed'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  {interview.status === 'completed' && <CheckCircle2 className="w-3 h-3" />}
                  {interview.status.charAt(0).toUpperCase() + interview.status.slice(1).replace('_', ' ')}
                </span>
              )}
            </div>
            <p className="text-xs sm:text-sm text-gray-500">
              {formatDateTime(interview.started_at || interview.created_at)}
              {(interview.started_at || interview.created_at) && ' • '}
              {formatTime(interview.started_at || interview.created_at)}
            </p>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-gray-100 rounded-xl text-gray-400 hover:text-gray-700 transition-colors shrink-0"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-5 sm:p-6 overflow-y-auto flex-1 min-h-0 space-y-6">
          {/* Overall Score */}
          <div className="text-center py-4 px-6 bg-gradient-to-b from-indigo-50/50 to-purple-50/30 rounded-2xl border border-indigo-100/50">
            <div className="inline-flex items-center justify-center w-24 h-24 sm:w-28 sm:h-28 bg-gradient-to-tr from-indigo-600 to-purple-600 rounded-full text-white text-2xl sm:text-3xl font-extrabold shadow-lg shadow-indigo-500/25 mb-3">
              {scoreNum !== null ? `${scoreNum.toFixed(1)}%` : 'N/A'}
            </div>
            <h3 className="text-base sm:text-lg font-bold text-gray-900">
              {t('myDetails.modals.interviewDetail.overallPerformance', 'Overall Performance')}
            </h3>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              {durationStr !== 'N/A' ? durationStr : t('myDetails.modals.interviewDetail.minutes', { count: interview.duration_minutes || 0 })}
              {' • '}
              {t('myDetails.modals.interviewDetail.questions', { count: questionsCount })}
            </p>
          </div>

          {/* Category Breakdown */}
          {categoryEntries.length > 0 ? (
            <div>
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                {t('myDetails.modals.interviewDetail.categoryBreakdown', 'Category Breakdown')}
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                {categoryEntries.map(([category, val]) => {
                  const score = (val !== null && typeof val === 'object') ? (val.score ?? 0) : (val ?? 0);
                  const numScore = Number.isFinite(Number(score)) ? Number(score) : 0;
                  return (
                    <div key={category} className={`p-4 rounded-xl border ${getScoreColor(numScore)} transition-all`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 min-w-0">
                          {getCategoryIcon(category)}
                          <span className="capitalize font-semibold text-xs sm:text-sm truncate">
                            {t(`myDetails.modals.interviewDetail.categories.${category}`, { defaultValue: category.replace('_', ' ') })}
                          </span>
                        </div>
                        <span className="text-lg sm:text-xl font-black shrink-0 ml-2">
                          {numScore.toFixed(0)}%
                        </span>
                      </div>
                      <div className="w-full bg-black/10 rounded-full h-1.5 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-current"
                          style={{ width: `${Math.min(100, Math.max(0, numScore))}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          {/* Feedback Summary */}
          <div className="bg-blue-50/60 p-5 rounded-2xl border border-blue-100">
            <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2 text-sm">
              <MessageSquare className="w-4 h-4 text-blue-600" />
              <span>{t('myDetails.modals.interviewDetail.interviewerFeedback', 'Interviewer Feedback')}</span>
            </h4>
            <p className="text-xs sm:text-sm text-gray-700 leading-relaxed whitespace-pre-line">
              {summary || t('myDetails.modals.interviewDetail.noFeedback', 'Feedback summary is being processed or not available for this session.')}
            </p>
          </div>

          {/* Interview Timeline */}
          <div className="bg-gray-50/70 p-5 rounded-2xl border border-gray-100">
            <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2 text-sm">
              <Clock className="w-4 h-4 text-gray-600" />
              <span>{t('myDetails.modals.interviewDetail.timeline', 'Interview Timeline')}</span>
            </h4>
            <div className="space-y-2.5 text-xs sm:text-sm">
              <div className="flex items-center justify-between py-1 border-b border-gray-200/60">
                <span className="text-gray-500">{t('myDetails.modals.interviewDetail.startedAt', 'Started At')}</span>
                <span className="font-medium text-gray-800">{formatTime(interview.started_at)}</span>
              </div>
              <div className="flex items-center justify-between py-1 border-b border-gray-200/60">
                <span className="text-gray-500">{t('myDetails.modals.interviewDetail.endedAt', 'Ended At')}</span>
                <span className="font-medium text-gray-800">{formatTime(interview.ended_at)}</span>
              </div>
              <div className="flex items-center justify-between py-1">
                <span className="text-gray-500">{t('myDetails.modals.interviewDetail.totalDuration', 'Total Duration')}</span>
                <span className="font-medium text-gray-800">{durationStr}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 sm:p-5 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between gap-3 shrink-0">
          <button
            onClick={() => {
              onClose();
              navigate('/progress');
            }}
            className="text-xs sm:text-sm font-semibold text-indigo-600 hover:text-indigo-700 flex items-center gap-1 group transition-colors"
          >
            {t('myDetails.modals.interviewDetail.viewProgress', 'View Full Progress & Analytics')}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs sm:text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors shadow-sm"
          >
            {t('common.close', 'Close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InterviewDetailModal;
